import React, { useContext, useEffect, useState } from 'react'
import Boom from 'boom'
import './styles.scss'

import contractContext from '../../../../../store/contexts/contractContext'
import CONTRACT_ACTIONS from '../../../../../store/actions/contractActions'

import IconBox from '../../../../../components/IconBox'
import UserInfoRow from '../../../../../components/UserInfoRow'
import InsuranceEvalComponent from '../../../../../components/InsuranceEvalComponent'
import Button from '../../../../../components/Button'
import PropertyCard from '../../../../../components/PropertyCard'

import { announceProperty } from '../../../../../services/properties'
import {
  createProposition,
  createInsurance,
  insuranceCheck,
  createContract,
} from '../../../../../services/contract'

import VerifiedShield from '../../../../../assets/svgs/VerifiedShield'
import Bill from '../../../../../assets/svgs/Bill'

import CONTRACT_STEPS from '../../../../../constants/contract-steps'

import get from 'lodash/get'

import { currencyFloatToString } from '../../../../../helpers/currency'
import {
  createContractObject,
  generateAnnouncementObject,
  createProposalObject,
  createPropositionProposal,
} from '../../../../../helpers/contract'
import { WARRANTY_TYPE } from 'constants/warrantyType'
import ErrorComponent from 'components/ErrorComponent'
import COLORS from 'styles/colors'
import { ContractContext } from 'types/contractContext'

const INSURANCE_RETRY_TIMER = 5000
const INSURANCE_CHECK_TIMEOUT = 120000

export default function Review() {
  const context: ContractContext = useContext(contractContext)
  const [isInsuranceCronRunning, setInsuranceCron] = useState(false)
  const [hasInsuranceTimedOut, setInsuranceTimeout] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const {
    proposition: {
      details: { type },
    },
    handleStepChange,
  } = context
  const { accessToken } = context.auth.userData

  useEffect(() => {
    handleInsuranceCheck()
    // eslint-disable-next-line
  }, [isInsuranceCronRunning])

  useEffect(() => {
    createContractProcess()
    // eslint-disable-next-line
  }, [])

  const createContractProcess = async () => {
    const {
      proposition: { id },
    } = context

    if (!id) {
      await createInitialData()
      return
    }

    await editInsuranceCheckData()
  }

  const createInitialData = async () => {
    setErrors([])
    const {
      contract: { contractDispatch },
      setIsLoading,
    } = context

    setIsLoading(true)
    //@ts-ignore
    const id = get(context, 'contract.propertyId', null) || (await announceCurrentProperty())
    try {
      const proposition = await createProposal(id)

      if (!proposition) {
        throw Boom.forbidden()
      }

      if (get(context, 'proposition.details.type') === WARRANTY_TYPE.INSURANCE) {
        const response = await createInsuranceProposal(id, proposition.id)
        if (!response.data) {
          setErrors([
            'Não foi possível criar proposta de seguro. Por favor consultar suporte, ou tente novamente mais tarde',
          ])
          setIsLoading(false)
          return
        }

        const entity = get(response, 'data.entity')

        contractDispatch({
          type: CONTRACT_ACTIONS.CHECK_INSURANCE,
        })
        contractDispatch({
          type: CONTRACT_ACTIONS.CHECK_INSURANCE_SUCCESSFULLY_CREATED,
          value: entity.id,
        })
      }

      setIsLoading(false)
      setInsuranceCron(true)
    } catch (e) {
      console.log({ e })
      setErrors([
        'Não foi possível criar proposta de seguro. Por favor consultar suporte, ou tente novamente mais tarde',
      ])
      setIsLoading(false)
    }
  }

  const announceCurrentProperty = async () => {
    const { contractDispatch } = context.contract
    const {
      auth: {
        userData: { id: user_id },
      },
      contract: { address, info, tenant_pays, values },
      proposition: {
        details: { type },
      },
    } = context

    const property = generateAnnouncementObject({
      insurance_type: type,
      user_id,
      address: {
        ...address,
        complement: address.complement || '-',
      },
      info,
      tenant_pays,
      values,
    })

    const {
      data: { id },
    } = await announceProperty(property, accessToken)

    contractDispatch({
      type: CONTRACT_ACTIONS.ANNOUNCE_PROPERTY,
      value: id,
    })
    return id
  }

  const createInsuranceProposal = async (id, propositionId) => {
    const { contract, proposition } = context

    const insuranceProposal = createPropositionProposal({
      id,
      propositionId,
      contract,
      proposition,
    })

    return createInsurance(accessToken, insuranceProposal)
  }

  const createProposal = async (propertyId) => {
    setErrors([])
    const {
      proposition: { details, propositionDispatch },
      contract: { tenant_pays, values, users: tenants },
      auth: {
        userData: { id: landlord_id },
      },
      setIsLoading,
    } = context

    const proposal = createProposalObject({
      propertyId,
      details,
      values,
      tenant_pays,
      landlord_id,
      tenants,
    })

    try {
      const res: any = await createProposition(accessToken, proposal)
      propositionDispatch({ type: CONTRACT_ACTIONS.SET_PROPOSITION_ID, value: res.id })

      return res
    } catch (e) {
      if (get(e, 'data.code') === 'proposition_not_updatable') {
        setErrors(['Não foi possível criar o contrato pois já existe um contrato esperando assinatura. Por favor consultar suporte.'])
      } else {
        setErrors(['Não foi possível criar o contrato. Por favor consultar suporte.'])
      }
      setIsLoading(false)
    }
  }

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
  const handleInsuranceCheck = async () => {
    const {
      contract: { contractDispatch },
      proposition: {
        details: { type },
      },
    } = context
    if (isInsuranceCronRunning && type === WARRANTY_TYPE.INSURANCE) {
      const {
        insurance: { id },
      } = context.contract
      let insuranceCheckResult
      const allowedTries = Math.floor(INSURANCE_CHECK_TIMEOUT / INSURANCE_RETRY_TIMER)
      let tries = 0

      while (
        get(insuranceCheckResult, 'data.status', 'PENDING') === 'PENDING' &&
        isInsuranceCronRunning &&
        tries < allowedTries
      ) {
        await delay(INSURANCE_RETRY_TIMER)
        insuranceCheckResult = await insuranceCheck(accessToken, id)
        tries += 1
      }

      setInsuranceCron(false)
      if (tries >= allowedTries) {
        setInsuranceTimeout(true)
      }

      contractDispatch({
        type: CONTRACT_ACTIONS.CHECK_INSURANCE_SUCCESSFULLY_CHECKED,
        value: {
          insurer: get(insuranceCheckResult, 'data.insurer'),
          status: get(insuranceCheckResult, 'data.status'),
        },
      })
    }
  }

  const editInsuranceCheckData = async () => {
    const {
      contract: { contractDispatch, propertyId: id },
      proposition,
      setIsLoading,
    } = context

    contractDispatch({
      type: CONTRACT_ACTIONS.RETRY_INSURANCE_CHECK,
    })
    setIsLoading(true)

    const response = await createInsuranceProposal(id, proposition.id)
    if (!response.data) {
      setErrors([
        'Não foi possível criar proposta de seguro. Por favor consultar suporte, ou tente novamente mais tarde',
      ])
      return
    }

    const entity = get(response, 'data.entity')

    contractDispatch({
      type: CONTRACT_ACTIONS.CHECK_INSURANCE_SUCCESSFULLY_CREATED,
      value: entity.id,
    })

    setIsLoading(false)
    setInsuranceCron(true)
  }

  const handleInsuranceRetry = () => {
    const { contractDispatch } = context.contract

    contractDispatch({
      type: CONTRACT_ACTIONS.RETRY_INSURANCE_CHECK,
    })

    setInsuranceTimeout(false)
    setInsuranceCron(true)
  }

  const getInsuranceCheckStatus = () => {
    if (hasInsuranceTimedOut) {
      return 'REFUSED'
    }

    const { isLoading, status } = context.contract.insurance
    const loading = isLoading ? 'LOADING' : null

    return loading || status
  }

  const handleCreateContract = async () => {
    const {
      proposition: { details },
      contract,
      auth: { userData: landlord },
      proposition,
      setStep,
      setIsLoading,
    } = context

    const contractObject = createContractObject({
      details,
      contract,
      landlord,
      proposition,
    })

    setIsLoading(true)

    try {
      await createContract(accessToken, contractObject)
    } catch (e) {
      console.log('Error creating contract', { e })
      setErrors([
        'Não foi possível criar o contrato.',
        'Verifique se o inquilino aceitou a proposta antes de criar o contrato.',
      ])
      setIsLoading(false)
      return
    }
    setIsLoading(false)

    setStep(CONTRACT_STEPS.FINISHED)
  }
  const { rent, iptu, condo } = context.contract.values

  //@ts-ignore
  const [firstIconBox, secondIconBox] = (() => {
    const { bond_months } = context.proposition.details
    const month = bond_months === 1 ? 'MÊS' : 'MESES'
    const depositLabel =
      bond_months === 0
        ? 'SEM CAUÇÃO DE ENTRADA'
        : `0${bond_months} ${month} DE ALUGUEL COMO CAUÇÃO DE ENTRADA`
    return [
      {
        label: depositLabel,
        Icons: [{ Icon: VerifiedShield, id: 'VerifiedShield' }],
      },
      { label: 'PAGAMENTO EM DINHEIRO', Icons: [{ Icon: Bill, id: 'Bill' }] },
    ]
  })()

  const handleGoToAddTenants = () => {
    handleStepChange('TenantRegister')

    //@ts-ignore
    document.getElementById('TenantRegister').scrollIntoView({ block: 'start', behavior: 'smooth' })
  }

  const isButtonDisabled = type === WARRANTY_TYPE.INSURANCE && getInsuranceCheckStatus() !== 'APPROVED'
  const years = get(context, 'proposition.details.duration')

  const withInsuranceRent = type === WARRANTY_TYPE.INSURANCE ? rent * 1.15 : rent

  return (
    <div className='review-container'>
      <ErrorComponent errors={errors} />
      <section>
        <h3>Partes do contrato</h3>
        {context.contract.users.map((user) => (
          <UserInfoRow key={user.id} name={user.name} email={user.email} className='vertical-margin' />
        ))}
      </section>

      <section>
        <h3>Dados do imóvel</h3>
        <PropertyCard selected={true} info={context.contract.info} address={context.contract.address} />
      </section>

      <section>
        <h3>Condições Negociadas</h3>
        <p>
          Abaixo se encontram todas as condições negociadas e adicionadas ao seu contrato de aluguel!
        </p>
        <div className='centralizer'>
          <div className='negotiated-conditions'>
            <div className='condition-value-container'>
              <span className='condition-label'>Aluguel</span>
              <span>{currencyFloatToString(withInsuranceRent)}</span>
            </div>

            <div className='condition-value-container'>
              <span className='condition-label'>Condomínio</span>
              <span>{currencyFloatToString(condo)}</span>
            </div>

            <div className='condition-value-container'>
              <span className='condition-label'>IPTU</span>
              <span>{currencyFloatToString(iptu)}</span>
            </div>
          </div>
        </div>


        <h4 style={{ margin: 0 }}>Vencimento do Aluguel e Data de Entrada</h4>
        <span style={{
          fontSize: 16,
          fontWeight: 700,
          color: COLORS.brand.primaryDark,
          margin: 8,
          display: 'block',
        }}>
          {new Date(context.proposition.details.payday).toLocaleDateString()}
        </span>

        <p>O dia do vencimento também será o dia do inicio do contrato e data de cobrança mensal.</p>
      </section>

      <section>
        <h3>Modelo de garantia e pagamento</h3>

        <div className='centralizer'>
          <div className='space-evenly'>
            <div className='icon-wrapper'>
              <IconBox
                id='b'
                label={firstIconBox.label}
                Icons={firstIconBox.Icons}
                textStyle={{ fontSize: '1em' }}
                expository
              />
            </div>
            <div className='icon-wrapper'>
              <IconBox id='a' label={secondIconBox.label} Icons={secondIconBox.Icons} expository />
            </div>
          </div>
        </div>
      </section>

      <section>
        <h3>Duração do contrato</h3>
        <p>Renovação automática e reajuste anual pelo IGPM*. Multa de 10% do valor restante da duração</p>
        <div className='centralizer'>
          <div className='years-amount'>
            <span>{`0${years}`}</span>
            <span>{`ano${years > 1 ? 's' : ''}`}</span>
          </div>
        </div>
        <p>Encaminharemos o contrato assinado para todas as partes</p>
        {type === WARRANTY_TYPE.INSURANCE && !errors.length && (
          <div className='centralizer'>
            <InsuranceEvalComponent
              status={getInsuranceCheckStatus()}
              insurer={get(context, 'contract.insurance.insurer')}
              className='insurance-container-styler'
              timedOut={hasInsuranceTimedOut}
              onClickTimeout={handleInsuranceRetry}
              tenantsAddAction={handleGoToAddTenants}
            />
          </div>
        )}
      </section>

      <div style={{ margin: '16px 0' }}>
        <ErrorComponent errors={errors} />
      </div>

      <div className='end-aligner'>
        <div className='button-footer'>
          <Button title='Voltar' onClick={() => handleStepChange('ContractDuration')} />
          <Button
            disabled={isButtonDisabled || !!errors.length}
            title='Criar Contrato'
            onClick={handleCreateContract}
            id='create-contract-button'
          />
        </div>
      </div>
    </div>
  )
}
